import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw } from 'lucide-react';
import { aiInterviewsAPI, errMsg } from '../../../lib/aiInterviewApi';
import { downloadBlob, fmtDate, Loading, Section, PROCTORING } from './common';

const RET = [['recordings_days', 'Recordings & snapshots'], ['reports_days', 'Reports & transcripts'], ['biometric_days', 'ID photos & selfies']];
const DIM_LABEL = { language: 'Language', device: 'Device', accommodation: 'Accommodation', proctoring_level: 'Proctoring level' };
const pct = (v) => `${Math.round((v || 0) * 100)}%`;

function SettingsForm() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);
  useEffect(() => { aiInterviewsAPI.settings().then(({ data }) => setS(data)).catch(() => toast.error('Could not load settings')); }, []);
  if (!s) return <Loading />;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await aiInterviewsAPI.saveSettings({
        retention: s.retention, languages: s.languages, default_proctoring: s.default_proctoring, default_duration_min: Number(s.default_duration_min),
        invite_expiry_days: Number(s.invite_expiry_days), integrity_weights: s.integrity_weights, webhook_url: s.webhook_url, ...extra,
      });
      setS(data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(errMsg(e, 'Could not save settings'));
    } finally {
      setBusy(false);
    }
  };
  const lang = (code) => set('languages', s.languages.includes(code) ? s.languages.filter((x) => x !== code) : [...s.languages, code]);
  return (
    <Section title="Interview settings" testId="settings-form" right={<button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={busy} onClick={() => save()} data-testid="settings-save-btn">{busy ? 'Saving…' : 'Save settings'}</button>}>
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {RET.map(([k, l]) => (
          <label key={k} className="iv-small" style={{ display: 'grid', gap: '0.3rem' }}>Keep {l.toLowerCase()} (days)
            <input type="number" min={1} max={3650} className="iv-input" value={s.retention[k]} onChange={(e) => set('retention', { ...s.retention, [k]: e.target.value })} data-testid={`settings-retention-${k}`} />
          </label>
        ))}
        <label className="iv-small" style={{ display: 'grid', gap: '0.3rem' }}>Default proctoring
          <select className="iv-select" value={s.default_proctoring} onChange={(e) => set('default_proctoring', e.target.value)} data-testid="settings-default-proctoring">
            {Object.entries(PROCTORING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="iv-small" style={{ display: 'grid', gap: '0.3rem' }}>Default duration (min)
          <input type="number" min={10} max={60} className="iv-input" value={s.default_duration_min} onChange={(e) => set('default_duration_min', e.target.value)} data-testid="settings-default-duration" />
        </label>
        <label className="iv-small" style={{ display: 'grid', gap: '0.3rem' }}>Invite link expiry (days)
          <input type="number" min={1} max={60} className="iv-input" value={s.invite_expiry_days} onChange={(e) => set('invite_expiry_days', e.target.value)} data-testid="settings-invite-expiry" />
        </label>
        <div className="iv-small" style={{ display: 'grid', gap: '0.3rem' }}>Interview languages
          <div className="iv-row">
            {[['en', 'English'], ['hi', 'Hindi']].map(([c, l]) => (
              <label key={c} className="iv-row" style={{ gap: '0.3rem' }}><input type="checkbox" checked={s.languages.includes(c)} onChange={() => lang(c)} data-testid={`settings-language-${c}`} /> {l}</label>
            ))}
          </div>
        </div>
      </div>
      <div style={{ marginTop: '1rem', display: 'grid', gap: '0.3rem' }}>
        <label className="iv-small" htmlFor="wh-url">Webhook URL (HTTPS) for events like ai_interview.completed and integrity.flagged</label>
        <input id="wh-url" className="iv-input" value={s.webhook_url || ''} onChange={(e) => set('webhook_url', e.target.value)} placeholder="https://example.com/hooks/chire" data-testid="settings-webhook-url" />
        <div className="iv-row iv-small">
          Signing secret: <span className="iv-mono">{reveal ? s.webhook_secret : '••••••••••••'}</span>
          <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => setReveal((r) => !r)} data-testid="settings-reveal-secret-btn">{reveal ? 'Hide' : 'Reveal'}</button>
          <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => save({ rotate_webhook_secret: true })} data-testid="settings-rotate-secret-btn"><RefreshCw size={13} /> Rotate</button>
        </div>
      </div>
      <details style={{ marginTop: '1rem' }}>
        <summary className="iv-small" style={{ cursor: 'pointer', fontWeight: 700 }} data-testid="settings-weights-toggle">Integrity signal weights (points per occurrence)</summary>
        <div style={{ display: 'grid', gap: '0.4rem', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', marginTop: '0.6rem' }}>
          {Object.entries(s.signal_types || {}).map(([k, v]) => (
            <label key={k} className="iv-status iv-small">
              <span>{v.label} <span style={{ opacity: 0.6 }}>({v.severity})</span></span>
              <input type="number" min={0} max={60} className="iv-input" style={{ width: 70 }} value={s.integrity_weights?.[k] ?? v.weight}
                onChange={(e) => set('integrity_weights', { ...s.integrity_weights, [k]: Number(e.target.value) })} data-testid={`settings-weight-${k}`} />
            </label>
          ))}
        </div>
      </details>
    </Section>
  );
}

function Fairness() {
  const [f, setF] = useState(null);
  useEffect(() => { aiInterviewsAPI.fairness().then(({ data }) => setF(data)).catch(() => setF({ total: 0, dimensions: {} })); }, []);
  const exportCsv = async () => {
    try {
      const { data } = await aiInterviewsAPI.biasCsv();
      downloadBlob(data, 'chire-ai-interview-bias-audit.csv');
    } catch (e) {
      toast.error('Could not export the CSV');
    }
  };
  return (
    <Section title="Fairness & flag rates" testId="fairness-panel" right={<button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={exportCsv} data-testid="export-bias-audit-btn"><Download size={14} /> Export Bias Audit CSV</button>}>
      {!f ? <Loading /> : (
        <>
          <p className="iv-small" style={{ marginBottom: '0.75rem' }}>{f.total} completed interview{f.total === 1 ? '' : 's'} · largest flag-rate gap between groups (n≥5): <b data-testid="fairness-max-gap">{pct(f.max_flag_rate_gap)}</b></p>
          <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 620px), 1fr))' }}>
            {Object.entries(f.dimensions || {}).map(([dim, groups]) => (
              <div key={dim} className="iv-scroll-x" style={{ minWidth: 0 }}>
                <table className="iv-table iv-table-compact" data-testid={`fairness-table-${dim}`}>
                  <thead><tr><th>{DIM_LABEL[dim] || dim}</th><th>n</th><th>Flagged</th><th>Mandatory</th><th>Confirmed</th><th>Avg score</th></tr></thead>
                  <tbody>{groups.map((g) => <tr key={g.group} style={{ cursor: 'default' }}><td>{g.group}</td><td>{g.n}</td><td>{pct(g.flag_rate)}</td><td>{pct(g.mandatory_rate)}</td><td>{pct(g.confirmed_rate)}</td><td>{g.avg_score}</td></tr>)}</tbody>
                </table>
              </div>
            ))}
          </div>
          <div className="iv-note" style={{ marginTop: '0.75rem' }}>{f.note}</div>
        </>
      )}
    </Section>
  );
}

function UsageAndEvents() {
  const [usage, setUsage] = useState(null);
  const [events, setEvents] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    aiInterviewsAPI.usage().then(({ data }) => setUsage(data)).catch(() => setUsage({ by_month: {} }));
    aiInterviewsAPI.events().then(({ data }) => setEvents(data)).catch(() => setEvents([]));
    aiInterviewsAPI.dataSummary().then(({ data }) => setSummary(data)).catch(() => {});
  }, []);
  return (
    <div className="iv-grid2">
      <Section title="Recent events" testId="events-panel">
        {!events ? <Loading /> : !events.length ? <p className="iv-small">No events yet.</p> : (
          <div style={{ display: 'grid', gap: '0.35rem', maxHeight: 320, overflowY: 'auto' }}>
            {events.slice(0, 40).map((e) => (
              <div key={e.id} className="iv-status iv-small"><span className="iv-mono">{e.type}</span><span>{fmtDate(e.created_at)}{e.webhook_status ? ` · webhook ${e.webhook_status}` : ''}</span></div>
            ))}
          </div>
        )}
      </Section>
      <div>
        <Section title="Usage" testId="usage-panel">
          {!usage ? <Loading /> : !Object.keys(usage.by_month).length ? <p className="iv-small">No completed interviews yet.</p> : (
            Object.entries(usage.by_month).map(([m, v]) => <div key={m} className="iv-status iv-small"><span>{m}</span><span>{v.interviews} interviews · {v.minutes} min</span></div>)
          )}
          <p className="iv-small" style={{ marginTop: '0.5rem' }}>{usage?.credits_per_interview ?? 1} credit per started interview.</p>
        </Section>
        {summary && (
          <Section title="Data processing summary" testId="data-summary-panel">
            <details>
              <summary className="iv-small" style={{ cursor: 'pointer', fontWeight: 700 }}>What is collected, why, and for how long</summary>
              <div className="iv-small" style={{ display: 'grid', gap: '0.4rem', marginTop: '0.5rem' }}>
                <div><b>Purposes:</b> {summary.purposes.join('; ')}</div>
                <div><b>Never processed:</b> {summary.not_processed.join(', ')}</div>
                <div><b>Candidate rights:</b> {summary.candidate_rights.join('; ')}</div>
                <div><b>Frameworks:</b> {summary.frameworks.join(', ')}</div>
                <div><i>{summary.counsel_note}</i></div>
              </div>
            </details>
          </Section>
        )}
      </div>
    </div>
  );
}

export default function SettingsTab() {
  return (
    <div data-testid="settings-tab">
      <SettingsForm />
      <Fairness />
      <UsageAndEvents />
    </div>
  );
}
